// ---- Pure prompt-lane primitives (extracted from sim/fishing.ts, plan 25) ----
// The seed-deterministic falling-prompt pattern + the per-prompt meter judge
// that back BOTH lane minigames: fishing's reel-in (arrows / tension) and
// cooking's pan (flip-stir-season prompts / burn). sim/fishing.ts re-exports
// the original reel-named surface and delegates here, so the fishing parity
// tests pin THIS math — any change to the rng call order (dir pick →
// repeat-dodge → gap jitter) silently reshuffles every pattern and fails
// src/sim/fishing.test.ts. Same purity rule as every sim module: no server /
// index.ts imports, no DOM/Bun.*; the SEED is rolled by the caller (rng stays
// outside), only the deterministic expansion lives here.

/** Caller-supplied randomness (Math.random in the game, seeded in tests). */
export type Rng = () => number;

/** Deterministic PRNG — the seed→pattern expander both sides share. */
export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const randRange = (rng: Rng, min: number, max: number): number => min + rng() * (max - min);

/** W=0 A=1 S=2 D=3 (the lane order everywhere). */
export type LaneDir = 0 | 1 | 2 | 3;
export type LaneArrow = { t: number; dir: LaneDir };
export type LanePattern = { duration: number; arrows: LaneArrow[] };

/** s before the first prompt (mount + read time). */
export const LANE_LEAD_IN = 1.0;

/** Expand a seed into the prompt pattern. The caller computes count + pace
 *  from its own difficulty model (fish tier/fightMul, cook tier); ±20% gap
 *  jitter; no immediate same-lane repeats. rng call order is LOAD-BEARING
 *  (see header). */
export function genLanePattern(seed: number, count: number, gap: number, gapMin: number, leadIn: number = LANE_LEAD_IN): LanePattern {
  const rng = mulberry32(seed);
  const arrows: LaneArrow[] = [];
  let t = leadIn;
  let prev = -1;
  for (let i = 0; i < count; i++) {
    let dir = Math.floor(rng() * 4) as LaneDir;
    if (dir === prev) dir = ((dir + 1 + Math.floor(rng() * 3)) % 4) as LaneDir;
    arrows.push({ t, dir });
    prev = dir;
    t += Math.max(gapMin, gap * randRange(rng, 0.8, 1.2));
  }
  return { duration: t, arrows };
}

export type LaneJudge = {
  pattern: LanePattern;
  /** ± s a press counts — the client's presentation window AND the half-width
   *  of the server's honest-arrival check in the input handler. */
  hitWindow: number;
  /** Next unjudged prompt — strictly monotonic (the wire's exactly-next rule). */
  idx: number;
  /** 0..1 failure meter (fishing reads it as tension, cooking as burn);
   *  reaching 1 fails the pattern. */
  tension: number;
};

/** "landed" = pattern cleared; "escaped" = the meter overflowed. The skills
 *  map these to their own fictions (fish landed/escaped, dish cooked/burnt). */
export type LaneEvent = { kind: "landed" } | { kind: "escaped" };

/** Per-prompt meter arithmetic — miss adds, hit relieves. */
export type LaneFx = { miss: number; relief: number };

const LANDED: LaneEvent[] = [{ kind: "landed" }];
const ESCAPED: LaneEvent[] = [{ kind: "escaped" }];
const NO_LANE_EVENTS: LaneEvent[] = [];

export function startLaneJudge(pattern: LanePattern, hitWindow: number): LaneJudge {
  return { pattern, hitWindow, idx: 0, tension: 0 };
}

/** Judge the next prompt. Fails the moment the meter reaches 1 (mid-pattern);
 *  lands when the last prompt resolves with the meter still under it. */
export function applyLaneResult(j: LaneJudge, hit: boolean, fx: LaneFx): LaneEvent[] {
  if (j.idx >= j.pattern.arrows.length) return NO_LANE_EVENTS; // already resolved
  j.idx++;
  if (hit) j.tension = Math.max(0, j.tension - fx.relief);
  else j.tension += fx.miss;
  if (j.tension >= 1) return ESCAPED;
  return j.idx >= j.pattern.arrows.length ? LANDED : NO_LANE_EVENTS;
}

/** Deadline resolution (AFK / stalled input): every unjudged prompt is a miss. */
export function forceResolveLane(j: LaneJudge, fx: LaneFx): LaneEvent[] {
  while (j.idx < j.pattern.arrows.length) {
    const ev = applyLaneResult(j, false, fx);
    if (ev.length) return ev;
  }
  return NO_LANE_EVENTS; // was already resolved
}
